/** Таблица книг — правая панель каталога. */

import { useEffect, useRef } from 'react'
import type { BookDetails, BookSummary } from '../api'
import { formatSize } from '../api'
import { Spinner } from './Spinner'

const COLUMNS: { key: string; label: string; sortable: boolean; className: string }[] = [
  { key: 'title', label: 'Название', sortable: true, className: 'col-title' },
  { key: 'author', label: 'Автор', sortable: true, className: 'col-author' },
  { key: 'series', label: 'Серия', sortable: true, className: 'col-series' },
  { key: 'edition', label: 'Издание', sortable: false, className: 'col-edition' },
  { key: 'year', label: 'Год', sortable: true, className: 'col-year' },
  { key: 'format', label: 'Формат', sortable: false, className: 'col-format' },
]

interface Props {
  books: BookSummary[]
  /** Подробности для книг-двойников: без них издания в списке неразличимы. */
  details: Record<string, BookDetails>
  total: number
  loading: boolean
  sort: string
  selectedId: number | null
  onSort: (key: string) => void
  onOpen: (book: BookSummary) => void
  onAuthor: (id: number, name: string) => void
  onSeries: (id: number, name: string) => void
  onLoadMore: () => void
}

export function BookTable({
  books,
  details,
  total,
  loading,
  sort,
  selectedId,
  onSort,
  onOpen,
  onAuthor,
  onSeries,
  onLoadMore,
}: Props) {
  const sentinel = useRef<HTMLDivElement>(null)

  // Подгрузка следующей страницы, когда низ таблицы появляется на экране.
  // На старых браузерах читалок IntersectionObserver может отсутствовать —
  // тогда автоподгрузка просто выключается, вместо того чтобы уронить каталог.
  useEffect(() => {
    const element = sentinel.current
    if (!element || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && books.length < total) onLoadMore()
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [books.length, loading, onLoadMore, total])

  if (!loading && books.length === 0) {
    return <div className="table__empty">Ничего не найдено</div>
  }

  return (
    <div className="table">
      <table>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                className={`${column.className}${column.sortable ? ' sortable' : ''}${
                  sort === column.key ? ' sorted' : ''
                }`}
                onClick={column.sortable ? () => onSort(column.key) : undefined}
              >
                {column.label}
                {sort === column.key && <span className="table__arrow">▾</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {books.map((book) => (
            <tr
              key={book.id}
              className={book.id === selectedId ? 'selected' : ''}
              onClick={() => onOpen(book)}
            >
              <td className="col-title">
                <span className={book.readable ? '' : 'dim'}>{book.title}</span>
              </td>
              <td className="col-author">
                {book.authors.map((author, index) => (
                  <span key={author.id}>
                    {index > 0 && ', '}
                    <button
                      className="linkish"
                      onClick={(event) => {
                        // Щелчок по автору не должен открывать книгу.
                        event.stopPropagation()
                        onAuthor(author.id, author.name)
                      }}
                    >
                      {author.name}
                    </button>
                  </span>
                ))}
              </td>
              <td className="col-series">
                {book.series && (
                  <button
                    className="linkish"
                    onClick={(event) => {
                      event.stopPropagation()
                      onSeries(book.series!.id, book.series!.name)
                    }}
                  >
                    {book.series.name}
                    {book.series_index ? ` (${Math.round(book.series_index)})` : ''}
                  </button>
                )}
              </td>
              <td className="col-edition">{editionHint(details[String(book.id)])}</td>
              <td className="col-year">{book.year ?? ''}</td>
              <td className="col-format">
                {book.formats.map((format) => (
                  <span key={format} className="badge">
                    {format}
                  </span>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {loading && <Spinner />}
      <div ref={sentinel} style={{ height: 1 }} />
    </div>
  )
}

/** Чем это издание отличается от соседнего: перевод, издательство, объём. */
function editionHint(details: BookDetails | undefined): string {
  if (!details) return ''
  if (details.translators.length) return `пер. ${details.translators.join(', ')}`
  const edition = [details.publisher, details.publish_year].filter(Boolean).join(', ')
  if (edition) return edition
  // Даже размер помогает: переводы одной книги заметно различаются объёмом.
  return details.size_bytes ? formatSize(details.size_bytes) : ''
}
